<template>
	<div v-if="category && categoryData" class="budget-cycle-page">
		<ja-header :title="category.name" :show-home="true" :show-intro="true">
			<template v-slot:icon>
				<svgicon :name="`expenseCategories/${category.assetName}`"></svgicon>
			</template>
		</ja-header>
		<content>
			<div class="category-section">
				<ja-research :data="categoryData" :category="category"></ja-research>
			</div>
			<div
				v-if="isSubmitDisabled"
				class="heading-4 mb--2 text--success text--center max-width--70"
			>
				<span v-if="haveToBudget">{{ $t('alreadyAppliedForLoan') }}</span>
				<span v-else>
					{{
						$t(`loanStatusMessages.${loanType}`, {
							autoLoanAmount: loanAmount,
							mortgageLoanAmount: loanAmount
						}) | noHtml
					}}
				</span>
			</div>
			<div class="category-section">
				<div class="form form--sm form--center form-col--2">
					<ja-input
						v-if="isAutoLoan"
						class="form-control"
						:label="$t('autoLoan')"
						:name="$t('autoLoan')"
						:aria-label="$t('autoLoan')"
						disabled
						:value="$t(AutoLoanType)"
					>
					</ja-input>
					<ja-input
						v-if="!JA_LOCALIZATION_IRL"
						class="form-control"
						:name="$t('creditScoreLbl')"
						:label="$t('creditScoreLbl')"
						:aria-label="$t('creditScoreLbl')"
						disabled
						:value="profileData.currentCreditScore"
					>
					</ja-input>
					<ja-input
						class="form-control"
						:name="$t('savingsAccountBalance')"
						:label="$t('savingsAccountBalance')"
						:aria-label="$t('savingsAccountBalance')"
						disabled
						:value="getTotalSavingsAmount() | currency"
					>
					</ja-input>
					<ja-input
						class="form-control"
						:name="$t('outstandingDebt')"
						:label="$t('outstandingDebt')"
						:aria-label="$t('outstandingDebt')"
						disabled
						:value="outstandingDebt | currency"
					>
					</ja-input>
					<ja-input
						class="form-control"
						:name="$t('netMonthlyIncome')"
						:label="$t('netMonthlyIncome')"
						:aria-label="$t('netMonthlyIncome')"
						disabled
						:value="netMonthlyIncome | currency"
					>
					</ja-input>
					<div class="btn-group btn-group--right">
						<ja-button
							v-if="haveToBudget"
							:name="$t('submit')"
							class="small mt--2"
							:success="hasAppliedForLoan"
							:disabled="isSubmitDisabled"
							@click.native="applyForLoan"
						>
							{{ $t('submit') }}
						</ja-button>
					</div>
				</div>
			</div>
		</content>
		<footer class="sticky-footer">
			<div>
				<ja-button
					class="small"
					name="finish-shopping"
					:aria-label="$t('finishShopping', { category: category.name })"
					:aria-disabled="!isSubmitDisabled"
					:disabled="!isSubmitDisabled"
					@click.once.native="finishShopping"
				>
					<span class="button-text">{{ $t('finish') }}</span>
				</ja-button>
			</div>
		</footer>
	</div>
</template>
<script>
import UserData from '@/mixins/UserData';
import ProfileData from '@/mixins/ProfileData';
import LifeStageData from '@/mixins/LifeStageData';
import Savings from '@/mixins/Savings';
import Debts from '@/mixins/Debts';
import Header from '../components/ui/headers/Header';
import Research from '../components/BudgetCycle/Research';
import ExpenseCategoryService from '@/services/ExpenseCategoryService';
import ExpenseCategories from '@/mixins/ExpenseCategories';
import graphqlClient from '@/helpers/graphqlClient';
import { Input } from '@/components/ui/form';
import { Button } from '@/components/ui';
import { applicationLoanTypes } from '@/helpers/constants';

export default {
	components: {
		jaHeader: Header,
		jaButton: Button,
		jaResearch: Research,
		jaInput: Input
	},
	filters: {
		// Since the text that is provided is with HTML
		// used in another places, we have to remove those html tags here
		// instead of adding exact same text in messages without the tags
		noHtml(value) {
			return value.replace(/<\/?[^>]+(>|$)/g, '');
		}
	},
	mixins: [
		UserData,
		LifeStageData,
		ProfileData,
		Savings,
		Debts,
		ExpenseCategories
	],
	props: {
		category: {
			type: Object,
			default: null
		}
	},
	data() {
		return {
			categoryData: null,
			hasAppliedForLoan: false,
			autoLoanOptions: [],
			loanOptions: null,
			JA_LOCALIZATION_IRL: this.$store.getters.isIrl
		};
	},
	computed: {
		billboard() {
			if (this.category.expenseCategory_ID) {
				return this.$store.getters.billboardByCategory(
					this.category.expenseCategory_ID
				);
			}
			return null;
		},
		haveToBudget() {
			if (this.category) {
				// isInBudgetingPhase comes from the lifeStage mixin
				return this.isInBudgetingPhase;
			}
			return false;
		},
		outstandingDebt() {
			return this.lifeStageData ? this.lifeStageData.outstandingDebt : 0;
		},
		isSubmitDisabled() {
			return this.hasAppliedForLoan;
		},
		AutoLoanType() {
			if (this.loanType === applicationLoanTypes.AUTO_LOAN) {
				return applicationLoanTypes.AUTO_LOAN;
			} else {
				return applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE;
			}
		},
		isAutoLoan() {
			if (
				this.loanType === applicationLoanTypes.AUTO_LOAN ||
				this.loanType === applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE
			) {
				return true;
			} else {
				return false;
			}
		},
		loanType() {
			if (
				this.category.key === applicationLoanTypes.MORTGAGE_LOAN &&
				!this.lifeStageData.isApprovedForMortgageLoan
			) {
				return 'disclaimer';
			}
			return this.category.key;
		},
		loanAmount() {
			let amount = 0;
			if (this.category && this.hasAppliedForLoan) {
				amount = this.lifeStageData.mortgageLoanAmount;
				if (
					this.category.key ===
					applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE
				) {
					amount = this.lifeStageData.autoLoanAmount;
				} else if (this.category.key === applicationLoanTypes.AUTO_LOAN) {
					amount = this.lifeStageData.autoLoanDealershipAmount;
				}
			}

			return this.$options.filters.currency(amount);
		}
	},
	async mounted() {
		if (this.category) {
			this.updateAutoLoanOptions();
			this.$route.meta.title = this.category.name;
			if (
				this.category.key !== applicationLoanTypes.MORTGAGE_LOAN &&
				this.category.key !==
					applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE &&
				this.$store.getters.autoLoan.hasApplied
			) {
				this.hasAppliedForLoan = await ExpenseCategoryService.hasAppliedForLoan(
					this.userData.studentId,
					this.$store.getters.autoLoan.expenseCategory_ID,
					this.lifeStageData.lifeStage_ID
				);
				return;
			} else if (
				this.category.key !== applicationLoanTypes.MORTGAGE_LOAN &&
				this.category.key !== applicationLoanTypes.AUTO_LOAN &&
				this.$store.getters.autoLoanBankingAndFinance.hasApplied
			) {
				this.hasAppliedForLoan = await ExpenseCategoryService.hasAppliedForLoan(
					this.userData.studentId,
					this.$store.getters.autoLoanBankingAndFinance.expenseCategory_ID,
					this.lifeStageData.lifeStage_ID
				);
				return;
			} else {
				this.hasAppliedForLoan = await ExpenseCategoryService.hasAppliedForLoan(
					this.userData.studentId,
					this.category.expenseCategory_ID,
					this.lifeStageData.lifeStage_ID
				);
				return;
			}
		}
	},
	created() {
		if (this.category) {
			this.loadCategoryData();
			this.setCategoryTabs();
		} else {
			this.$store.commit('setLoading', true);
			this.$router.push({ path: '/home' });
		}
	},
	beforeRouteLeave(to, from, next) {
		if (window.popStateDetected) {
			graphqlClient.stop();
			this.$store.commit('setLoading', true);
			this.$store.commit('resetLifeStageData');
			this.$store.commit('resetCategories');
			window.popStateDetected = false;
		}
		next();
	},
	methods: {
		async updateAutoLoanOptions() {
			const {
				autoLoan,
				autoLoanBankingAndFinance
			} = await ExpenseCategoryService.getLoanCategories(
				this.userData.studentFacilityId
			);
			this.userData.studentFacilityId;
			this.autoLoanOptions = [
				{
					name: this.$t(applicationLoanTypes.AUTO_LOAN_DEALERSHIP),
					expenseCategory_ID: autoLoan.expenseCategory_ID,
					assetName: autoLoan.assetName
				},
				{
					name: this.$t(applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE),
					expenseCategory_ID: autoLoanBankingAndFinance.expenseCategory_ID,
					assetName: autoLoanBankingAndFinance.assetName
				}
			];
		},
		async applyForLoan() {
			if (this.userData && this.lifeStageData) {
				ExpenseCategoryService.applyForLoan(
					this.userData.studentId,
					this.category.expenseCategory_ID,
					this.lifeStageData.lifeStage_ID
				).then(response => {
					this.hasAppliedForLoan = response;
				});
			}
		},
		finishShopping() {
			this.$store.commit('setLoading', true);
			this.$store.commit('resetLifeStageData');
			this.$store.commit('resetCategories');
			this.$router.push({ path: '/home' });
		},
		async loadCategoryData() {
			this.categoryData = await ExpenseCategoryService.getLoanApplicationCategoryData(
				this.category.expenseCategory_ID
			);
			this.$store.commit('setLoading', false);
		}
	}
};
</script>
<style lang="scss" scoped>
.budget-cycle-page {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;

	header {
		flex-shrink: 0;
	}

	content {
		display: flex;
		flex-direction: column;
		align-items: center;
		flex-grow: 1;
		overflow: auto;
		position: relative;

		.category-section {
			width: 100%;
			padding-bottom: 64px;

			&:last-child {
				padding-top: 64px;
				border-top: 1px solid $container-border;
			}
		}

		.slider-container {
			width: 75%;
			max-width: 768px;
			margin: 0 auto;
		}
	}

	footer {
		width: 100%;
		height: 76px;
		padding: 0 5%;
		display: flex;
		position: relative;
		z-index: 1;
		flex: 0 0 auto;
		align-items: center;
		justify-content: flex-end;
		background-color: $container-bgcolor;
		box-shadow: 0px 0px 4px -1px rgba(0, 0, 0, 0.45);
		flex-shrink: 0;
		width: 100%;
		display: flex;
		align-items: center;
		font-family: 'RobotoMedium';

		.outlined {
			margin-right: 16px;
		}

		.left-btn-wrapper {
			display: flex;
			flex-grow: 1;

			.range-wrapper {
				width: 30%;
				height: 100%;

				.range-labels {
					display: flex;
					align-items: center;

					h4 {
						flex-grow: 1;
						margin: 0;
						color: $font-blue;

						&.overflow {
							color: $budget-bar-overflow-color;
						}
					}

					.body-text-2 {
						color: $input-color;
					}
				}

				.budget-bar {
					::v-deep.filled:not(.overflow) {
						background-color: $primary-state;
					}
				}
			}
		}

		.navigation-btn-text {
			position: relative;
			top: 1px;
			font-family: 'RobotoMedium';
		}
	}
}
</style>
